import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { map } from 'rxjs/operators';
import { AuthService } from '../../../core/auth/auth.service';
import { SubscriptionService } from '../../../features/subscription/subscription.service';

interface NavItem {
  label: string;
  icon: string;
  route: string;
  allowedRoles: string[];
}

const ALL_ROLES = ['Responsavel', 'Secretario', 'EmbaixadorChefe', 'ConselheiroAuxiliar', 'Conselheiro'];

const ALL_NAV_ITEMS: NavItem[] = [
  { label: 'Home',       icon: 'home',            route: '/dashboard',  allowedRoles: ALL_ROLES },
  { label: 'ERs',        icon: 'group',            route: '/ers',        allowedRoles: ['Secretario', 'EmbaixadorChefe', 'ConselheiroAuxiliar', 'Conselheiro'] },
  { label: 'Chamadas',   icon: 'event_available',  route: '/chamadas',   allowedRoles: ['Secretario', 'EmbaixadorChefe', 'ConselheiroAuxiliar', 'Conselheiro'] },
  { label: 'Postos',     icon: 'military_tech',    route: '/postos',     allowedRoles: ALL_ROLES },
  { label: 'Relatórios', icon: 'bar_chart',        route: '/relatorios', allowedRoles: ['EmbaixadorChefe', 'ConselheiroAuxiliar', 'Conselheiro'] },
  { label: 'Usuários',   icon: 'manage_accounts',  route: '/usuarios',   allowedRoles: ['ConselheiroAuxiliar', 'Conselheiro'] },
  { label: 'Assinatura', icon: 'workspace_premium', route: '/assinatura', allowedRoles: ['Conselheiro'] },
];

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatMenuModule,
  ],
  styles: [`
    .shell-wrapper {
      display: flex;
      flex-direction: column;
      height: 100vh;
    }

    .toolbar {
      background: var(--er-navy);
      color: var(--er-white);
      position: sticky;
      top: 0;
      z-index: 100;
    }

    .toolbar-logo {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex: 1;
      text-decoration: none;
      color: inherit;
      cursor: pointer;
    }

    .toolbar-logo img {
      height: 32px;
      width: 32px;
      object-fit: contain;
    }

    .toolbar-logo span {
      font-size: 1.1rem;
      font-weight: 500;
    }

    .toolbar-logo .accent {
      color: var(--er-gold);
      font-weight: 700;
    }

    .avatar-btn { padding: 4px; }
    .avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: var(--er-gold);
      color: var(--er-navy);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.875rem;
      font-weight: 700;
    }
    .user-menu-header {
      padding: 12px 16px;
      min-width: 200px;
      cursor: default;
    }
    .user-menu-name { font-weight: 600; font-size: 0.9rem; }
    .user-menu-email { font-size: 0.8rem; color: #9e9e9e; }

    mat-sidenav-container {
      flex: 1;
    }

    mat-sidenav {
      width: 230px;
      background: var(--er-navy);
      border-right: none;
    }

    .sidenav-header {
      padding: 1.25rem 1rem 0.5rem;
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      color: rgba(255, 255, 255, 0.45);
      text-transform: uppercase;
    }

    .nav-list {
      padding: 0 0.5rem;
    }

    .nav-item {
      border-radius: 8px;
      margin-bottom: 2px;
      color: var(--er-white);
    }

    .nav-item mat-icon {
      color: var(--er-white);
    }

    ::ng-deep .nav-item .mdc-list-item__primary-text {
      color: var(--er-white) !important;
    }

    .nav-item:hover:not(.active-link) {
      background: rgba(255, 255, 255, 0.08) !important;
    }

    .nav-item.active-link {
      background: var(--er-gold) !important;
      color: var(--er-navy) !important;
      font-weight: 600;
    }

    .nav-item.active-link mat-icon {
      color: var(--er-navy) !important;
    }

    ::ng-deep .nav-item.active-link .mdc-list-item__primary-text {
      color: var(--er-navy) !important;
    }

    .main-content {
      padding: 1.5rem;
      background: var(--er-gray-bg);
      min-height: 100%;
    }

    .trial-banner {
      background: var(--er-gold);
      color: var(--er-navy);
      padding: 6px 16px;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.85rem;
      font-weight: 500;
    }

    .trial-banner mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
    }

    .trial-banner a {
      color: var(--er-navy);
      font-weight: 700;
      margin-left: auto;
      text-decoration: none;
    }
  `],
  template: `
    <div class="shell-wrapper">
      <!-- Toolbar -->
      <mat-toolbar class="toolbar">
        @if (isHandset()) {
          <button mat-icon-button (click)="sidenav.toggle()" aria-label="Menu">
            <mat-icon>menu</mat-icon>
          </button>
        }
        <a class="toolbar-logo" routerLink="/dashboard">
          <img src="logo_insignia_embaixadores_do_rei_er_transparente.svg" alt="Escudo ER" width="32" height="32" />
          <span>Embaixadores <span class="accent">do Rei</span></span>
        </a>
        <button mat-icon-button class="avatar-btn" [matMenuTriggerFor]="userMenu" aria-label="Menu do usuário">
          <div class="avatar">{{ userInitials() }}</div>
        </button>

        <mat-menu #userMenu="matMenu" xPosition="before">
          <div class="user-menu-header" (click)="$event.stopPropagation()">
            <div class="user-menu-name">{{ authService.currentUser()?.fullName }}</div>
            <div class="user-menu-email">{{ authService.currentUser()?.email }}</div>
          </div>
          <mat-divider />
          @if (authService.currentRole() === 'Conselheiro') {
            <button mat-menu-item routerLink="/assinatura">
              <mat-icon>workspace_premium</mat-icon>
              <span>Assinatura</span>
            </button>
          }
          <mat-divider />
          <button mat-menu-item (click)="logout()">
            <mat-icon>logout</mat-icon>
            <span>Sair</span>
          </button>
        </mat-menu>
      </mat-toolbar>

      <!-- Sidenav container -->
      <mat-sidenav-container>
        <mat-sidenav
          #sidenav
          [mode]="isHandset() ? 'over' : 'side'"
          [opened]="!isHandset()"
        >
          <div class="sidenav-header">Navegação</div>
          <mat-nav-list class="nav-list">
            @for (item of navItems(); track item.route) {
              <a
                mat-list-item
                class="nav-item"
                routerLinkActive="active-link"
                [routerLink]="item.route"
              >
                <mat-icon matListItemIcon>{{ item.icon }}</mat-icon>
                <span matListItemTitle>{{ item.label }}</span>
              </a>
            }
          </mat-nav-list>
        </mat-sidenav>

        <mat-sidenav-content>
          @if (subscriptionService.subscriptionStatus()?.status === 'Trial') {
            <div class="trial-banner">
              <mat-icon>hourglass_empty</mat-icon>
              <span>Período Trial — {{ daysRemaining() }} dias restantes</span>
              <a routerLink="/assinatura">Ver planos</a>
            </div>
          }
          <div class="main-content">
            <router-outlet />
          </div>
        </mat-sidenav-content>
      </mat-sidenav-container>
    </div>
  `,
})
export class MainLayoutComponent {
  readonly authService = inject(AuthService);
  readonly subscriptionService = inject(SubscriptionService);
  private readonly router = inject(Router);
  private readonly breakpointObserver = inject(BreakpointObserver);

  readonly daysRemaining = computed(() => {
    const trialEndsAt = this.subscriptionService.subscriptionStatus()?.trialEndsAt;
    if (!trialEndsAt) return 0;
    const end = new Date(trialEndsAt);
    const now = new Date();
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  });

  readonly userInitials = computed(() => {
    const name = this.authService.currentUser()?.fullName ?? '';
    if (!name.trim()) return '';
    return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
  });

  readonly navItems = computed(() => {
    const role = this.authService.currentRole();
    if (!role) return [];
    return ALL_NAV_ITEMS.filter(item => item.allowedRoles.includes(role));
  });

  readonly isHandset = toSignal(
    this.breakpointObserver.observe(Breakpoints.Handset).pipe(map((r) => r.matches)),
    { initialValue: false },
  );

  logout(): void {
    this.authService.logout().subscribe({
      next: () => this.router.navigate(['/login']),
      error: () => {
        this.authService.clearStorage();
        this.router.navigate(['/login']);
      },
    });
  }
}
